import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { initSentry, Sentry } from './lib/sentry';

// Sentry.ErrorBoundary's class-component signature collides with React
// 18's Component<any, any, any> shape under strict tsc on some pnpm
// resolutions (only surfaces on Vercel's Linux runners, not locally
// or in GitHub Actions CI). Casting through ComponentType silences the
// JSX-element check without changing runtime behavior. Mirror of the
// same workaround in apps/marketing/src/main.tsx.
const SentryErrorBoundary = Sentry.ErrorBoundary as unknown as React.ComponentType<{
  fallback: React.ReactNode;
  children?: React.ReactNode;
}>;
import { routeTree } from './routeTree.gen';
import { AuthProvider } from './lib/auth/auth-context';
import { TenantProvider } from './context/TenantContext';
import './index.css';
import 'mapbox-gl/dist/mapbox-gl.css';

initSentry();

const router = createRouter({ routeTree, defaultPreload: false });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

function FallbackError(): React.ReactElement {
  return (
    <main className="min-h-screen flex items-center justify-center bg-bg-soft px-6">
      <div className="text-center">
        <p className="text-xs uppercase tracking-wide text-ink-2 mb-2">LYN Rx</p>
        <h1 className="text-3xl font-semibold text-ink mb-2">Something went wrong</h1>
        <p className="text-sm text-ink-2">The error has been reported. Please refresh and try again.</p>
      </div>
    </main>
  );
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={<FallbackError />}>
      <AuthProvider>
        <TenantProvider>
          <RouterProvider router={router} />
        </TenantProvider>
      </AuthProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);
